/**
 * Design-system mode: the switch between the current (legacy) UI and the
 * experimental Modern UI.
 *
 * Resolution order:
 *   1. Developer env override (VITE_ENABLE_MODERN_UI) — forces 'modern'.
 *   2. Stored user preference in localStorage (`oneup.designSystem`).
 *   3. Default — 'legacy'.
 *
 * The env value is read in the APP layer (where Vite statically replaces
 * `import.meta.env.*`) and passed in as `envEnabled`, so this module stays
 * framework-agnostic and tree-shakeable.
 */

export type DesignSystemMode = 'legacy' | 'modern';

/** localStorage key — matches the requested `oneup.designSystem`. */
export const DESIGN_SYSTEM_STORAGE_KEY = 'oneup.designSystem';

export const DEFAULT_DESIGN_SYSTEM_MODE: DesignSystemMode = 'legacy';

export function isDesignSystemMode(value: unknown): value is DesignSystemMode {
  return value === 'legacy' || value === 'modern';
}

export function readStoredMode(): DesignSystemMode | null {
  try {
    const value = localStorage.getItem(DESIGN_SYSTEM_STORAGE_KEY);
    return isDesignSystemMode(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeStoredMode(mode: DesignSystemMode): void {
  try {
    localStorage.setItem(DESIGN_SYSTEM_STORAGE_KEY, mode);
  } catch {
    /* ignore (private mode / disabled storage) */
  }
}

export interface ResolveModeOptions {
  /** Result of reading VITE_ENABLE_MODERN_UI in the app. Forces 'modern'. */
  envEnabled?: boolean;
  /** Override the stored value (mainly for tests/SSR). */
  stored?: DesignSystemMode | null;
}

/**
 * Resolve the effective design-system mode. Defaults to Legacy unless the user
 * has opted in (localStorage) or a developer has forced it via env.
 */
export function getDesignSystemMode(options: ResolveModeOptions = {}): DesignSystemMode {
  const { envEnabled = false, stored } = options;
  if (envEnabled) return 'modern';
  const fromStore = stored !== undefined ? stored : readStoredMode();
  return fromStore ?? DEFAULT_DESIGN_SYSTEM_MODE;
}

/**
 * Helper for apps to read the env flag with the correct Vite variable name.
 * Call from app code where `import.meta.env` is statically replaced.
 */
export function parseEnvEnabled(rawValue: string | boolean | undefined): boolean {
  if (rawValue === true) return true;
  if (typeof rawValue !== 'string') return false;
  const v = rawValue.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'on' || v === 'yes';
}
